import { useEffect, useRef } from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { useNavigation } from "@/providers/navigation/NavigationContext";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import {
  ChatRecord,
  ChatRole,
  ChatStatus,
  ChatToolUseContent,
  ClientToolResult,
  isChatToolUseContent,
} from "../ChatRecord";
import { executeClientTool } from "../clientTools";

/**
 * Build a client tool result from a tool use content block and its execution result.
 */
function buildToolResult(
  toolUse: ChatToolUseContent,
  result: { response: Record<string, unknown>; success: boolean },
  runtimeMs: number,
): ClientToolResult {
  return {
    tool_name: toolUse.tool_name,
    tool_use_id: toolUse.tool_use_id,
    runtime_ms: runtimeMs,
    status: result.success ? "success" : "error",
    output: result.response,
  };
}

/**
 * Hook that watches a ChatRecord and automatically executes client-side tools
 * when the session enters CLIENT_TOOL_TURN status.
 *
 * When the LLM invokes a client tool, the backend sets the session status to
 * CLIENT_TOOL_TURN. This hook detects that state, executes the pending tools
 * in the browser, and submits the results back to the backend so the LLM can
 * continue generating.
 *
 * @param chat - The current chat record to watch, or undefined if no chat exists
 * @param orgId - The organization ID for API requests
 */
export function useClientToolExecution(
  chat: ChatRecord | undefined,
  orgId: string | undefined,
) {
  const { chatService } = useDomainServices();
  const { goto } = useNavigation();
  // Track the last executed state to prevent duplicate submissions
  const lastExecutedRef = useRef<string | null>(null);

  useEffect(() => {
    async function executePendingTools() {
      if (!chat || !orgId) {
        return;
      }
      if (chat.status !== ChatStatus.CLIENT_TOOL_TURN) {
        return;
      }

      // Find pending tool uses in the last assistant message
      const lastMessage = chat.messages[chat.messages.length - 1];
      if (!lastMessage || lastMessage.role !== ChatRole.ASSISTANT) {
        return;
      }

      const toolUses = lastMessage.content.filter(isChatToolUseContent);
      if (toolUses.length === 0) {
        return;
      }

      // Deduplicate: don't re-execute if we already handled this exact state
      const executionKey = `${chat.session_id}:${chat.messages.length}`;
      if (lastExecutedRef.current === executionKey) {
        return;
      }
      lastExecutedRef.current = executionKey;

      try {
        const toolResults: ClientToolResult[] = toolUses.map((toolUse) => {
          const startTime = performance.now();
          const inputParams = toolUse.input ?? {};
          const result = executeClientTool(toolUse.tool_name, inputParams, {
            goto,
          });
          const runtimeMs = Math.round(performance.now() - startTime);

          return buildToolResult(toolUse, result, runtimeMs);
        });

        await chatService.submitToolResults(
          chat.session_id,
          { tool_results: toolResults },
          { orgId },
        );
      } catch (error) {
        lastExecutedRef.current = null;
        ErrorMonitoringService.captureError(error as Error);
      }
    }

    void executePendingTools();
  }, [chat, orgId, chatService, goto]);
}
